import React, { createContext, useContext, useState, useRef, ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface ROIPoint {
    x: number;
    y: number;
}

interface CsvSiteDataRow {
    [key: string]: string | number | undefined;
}

export type CalibrationMode = 'apriltag' | 'manual';

export interface CalibrationResult {
    meters_per_pixel: number;
    std_deviation_pct: number;
    sample_count: number;
    frames_scanned: number;
}

interface ROIContextType {
    roiStart: ROIPoint | null;
    setRoiStart: (point: ROIPoint | null) => void;
    roiEnd: ROIPoint | null;
    setRoiEnd: (point: ROIPoint | null) => void;

    // Reference points
    referencePoints: ROIPoint[];
    setReferencePoints: (points: ROIPoint[]) => void;
    referenceLengthMeters: number;
    setReferenceLengthMeters: (length: number) => void;

    // Calibration mode (manual reference line vs. AprilTag auto-calibration)
    calibrationMode: CalibrationMode;
    setCalibrationMode: (mode: CalibrationMode) => void;
    calibrationResult: CalibrationResult | null;
    setCalibrationResult: (result: CalibrationResult | null) => void;

    // Site data (populated from site registration)
    channelDepth: number;
    setChannelDepth: (depth: number) => void;
    currentPowerProduction: number;
    setCurrentPowerProduction: (power: number) => void;
    siteName: string;
    setSiteName: (name: string) => void;
    coordinates: string;
    setCoordinates: (coords: string) => void;
    latitude: number;
    setLatitude: (lat: number) => void;
    longitude: number;
    setLongitude: (lng: number) => void;
    headHeight: number;
    setHeadHeight: (height: number) => void;
    yearBuilt: number;
    setYearBuilt: (year: number) => void;
    numberOfGates: number;
    setNumberOfGates: (gates: number) => void;
    numberOfSections: number;
    setNumberOfSections: (sections: number) => void;

    // CSV Site Registration Data (for monthly flow data)
    csvSiteData: CsvSiteDataRow[] | null;
    setCsvSiteData: (data: CsvSiteDataRow[] | null) => void;

    // Polygon ROI
    isDrawingPolygon: boolean;
    setIsDrawingPolygon: (drawing: boolean) => void;
    polygonPoints: ROIPoint[];
    setPolygonPoints: (points: ROIPoint[]) => void;
    isPolygonClosed: boolean;
    setIsPolygonClosed: (closed: boolean) => void;

    // Mouse tracking
    mousePosition: ROIPoint | null;
    setMousePosition: (position: ROIPoint | null) => void;

    // DOM references
    videoContainerRef: React.RefObject<HTMLDivElement>;
    imageRef: React.RefObject<HTMLImageElement>;

    // Actions
    resetROI: () => void;
    resetReferenceLine: () => void;
    resetAll: () => void;
    handleVideoClick: (e: React.MouseEvent<HTMLDivElement>) => void;
    handleMouseMove: (e: React.MouseEvent<HTMLDivElement>) => void;
}

const ROIContext = createContext<ROIContextType | undefined>(undefined);

// Export the context for the hook file
export { ROIContext };
export type { ROIContextType };

interface ROIProviderProps {
    children: ReactNode;
}

export const ROIProvider: React.FC<ROIProviderProps> = ({ children }) => {
    const location = useLocation();
    const [roiStart, setRoiStart] = useState<ROIPoint | null>(null);
    const [roiEnd, setRoiEnd] = useState<ROIPoint | null>(null);
    const [referencePoints, setReferencePoints] = useState<ROIPoint[]>([]);
    const [referenceLengthMeters, setReferenceLengthMeters] = useState<number>(1);
    const [calibrationMode, setCalibrationMode] = useState<CalibrationMode>('apriltag');
    const [calibrationResult, setCalibrationResult] = useState<CalibrationResult | null>(null);

    // Site data state (populated from site registration when video is selected)
    const [channelDepth, setChannelDepth] = useState<number>(0);
    const [currentPowerProduction, setCurrentPowerProduction] = useState<number>(0);
    const [siteName, setSiteName] = useState<string>('');
    const [coordinates, setCoordinates] = useState<string>('');
    const [latitude, setLatitude] = useState<number>(0);
    const [longitude, setLongitude] = useState<number>(0);
    const [headHeight, setHeadHeight] = useState<number>(0);
    const [yearBuilt, setYearBuilt] = useState<number>(0);
    const [numberOfGates, setNumberOfGates] = useState<number>(0);
    const [numberOfSections, setNumberOfSections] = useState<number>(0);

    const [csvSiteData, setCsvSiteData] = useState<CsvSiteDataRow[] | null>(null);
    const [isDrawingPolygon, setIsDrawingPolygon] = useState<boolean>(false);
    const [polygonPoints, setPolygonPoints] = useState<ROIPoint[]>([]);
    const [isPolygonClosed, setIsPolygonClosed] = useState<boolean>(false);
    const [mousePosition, setMousePosition] = useState<ROIPoint | null>(null);


    const videoContainerRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);

    const isFirstRender = React.useRef(true);


    const resetROI = React.useCallback(() => {
        setRoiStart(null);
        setRoiEnd(null);
        setPolygonPoints([]);
        setIsPolygonClosed(false);
    }, []);

    const resetReferenceLine = React.useCallback(() => {
        setReferencePoints([]);
        setReferenceLengthMeters(1);
        setCalibrationResult(null);
        setCalibrationMode('apriltag');
    }, []);

    const resetAll = React.useCallback(() => {
        resetROI();
        resetReferenceLine();
        setIsDrawingPolygon(false);
        setMousePosition(null);
        setCsvSiteData(null);
        // Reset site data
        setChannelDepth(0);
        setCurrentPowerProduction(0);
        setSiteName('');
        setCoordinates('');
        setLatitude(0);
        setLongitude(0);
        setHeadHeight(0);
        setYearBuilt(0);
        setNumberOfGates(0);
        setNumberOfSections(0);
    }, [resetROI, resetReferenceLine]);

    const handleVideoClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;

        if (isDrawingPolygon) {
            if (polygonPoints.length > 2) {
                const firstPoint = polygonPoints[0];
                const distToFirst = Math.sqrt(Math.pow(x - firstPoint.x, 2) + Math.pow(y - firstPoint.y, 2));

                if (distToFirst < 5) {
                    setIsPolygonClosed(true);
                    return;
                }
            }

            if (!isPolygonClosed) {
                setPolygonPoints([...polygonPoints, { x, y }]);
            }
        } else {
            if (calibrationMode === 'manual' && referencePoints.length < 2) {
                setReferencePoints([...referencePoints, { x, y }]);
            } else if (!roiStart) {
                setRoiStart({ x, y });
            } else if (!roiEnd) {
                setRoiEnd({ x, y });
            }
        }
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;

        setMousePosition({ x, y });

        if (roiStart && !roiEnd && !isDrawingPolygon) {
            setRoiEnd({ x, y });
        }
    };

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        resetAll();
    }, [location.pathname, resetAll]);
    useEffect(() => {
        const handleBeforeUnload = () => {
            resetAll();
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [resetAll]);

    return (
        <ROIContext.Provider
            value={{
                roiStart,
                setRoiStart,
                roiEnd,
                setRoiEnd,
                referencePoints,
                setReferencePoints,
                referenceLengthMeters,
                setReferenceLengthMeters,
                calibrationMode,
                setCalibrationMode,
                calibrationResult,
                setCalibrationResult,
                channelDepth,
                setChannelDepth,
                currentPowerProduction,
                setCurrentPowerProduction,
                siteName,
                setSiteName,
                coordinates,
                setCoordinates,
                latitude,
                setLatitude,
                longitude,
                setLongitude,
                headHeight,
                setHeadHeight,
                yearBuilt,
                setYearBuilt,
                numberOfGates,
                setNumberOfGates,
                numberOfSections,
                setNumberOfSections,
                csvSiteData,
                setCsvSiteData,
                isDrawingPolygon,
                setIsDrawingPolygon,
                polygonPoints,
                setPolygonPoints,
                isPolygonClosed,
                setIsPolygonClosed,
                mousePosition,
                setMousePosition,
                videoContainerRef,
                imageRef,
                resetROI,
                resetReferenceLine,
                resetAll,
                handleVideoClick,
                handleMouseMove,
            }}
        >
            {children}
        </ROIContext.Provider>
    );
};
